import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocalStorage } from "../../core/hooks/useLocalStorage";
import { useLogin } from "../hooks/useLogin";
import { useLogout } from "../hooks/useLogout";
import { useUserInfo } from "../hooks/useUserInfo";
import { UserInfo } from "../types/userInfo";
import { getUser } from "../../admin/infra/getUser";

interface AuthContextInterface {
  authKey: string;
  hasRole: (path: any, jsonPermissions?: any) => {};
  hasChildPermission: (modulePath: string, childKey: string) => boolean;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  login: (email: string, password: string) => Promise<any>;
  logout: () => Promise<any>;
  permissionsList: any;
  permissions: () => Promise<any>;
  userInfo?: UserInfo;
}

export const AuthContext = createContext({} as AuthContextInterface);

type AuthProviderProps = {
  children?: React.ReactNode;
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  console.log("AuthProvider");
  const [authKey, setAuthKey] = useLocalStorage<string>("authkey", "");
  const [permissionsData, setPermissions] = useState<any>([]);

  const { isLoggingIn, login, permissions } = useLogin();
  const { isLoggingOut, logout } = useLogout();

  const {
    data: userInfo,
    error: userInfoError,
    isError,
  } = useUserInfo(authKey);

  // Se houver erro ao carregar userInfo e temos authKey, limpar dados
  useEffect(() => {
    if (isError && userInfoError && authKey) {
      console.warn("Erro ao carregar userInfo, limpando authKey");
      setAuthKey("");
    }
  }, [isError, userInfoError, authKey, setAuthKey]);

  // Carregar permissões quando o usuário estiver logado
  useEffect(() => {
    let isMounted = true;

    if (authKey && permissionsData.length === 0) {
      // Tenta carregar do localStorage primeiro
      const storedPermissions = localStorage.getItem("rh-permissions");
      if (storedPermissions) {
        try {
          setPermissions(JSON.parse(storedPermissions));
        } catch (e) {
          console.warn("Erro ao parsear permissões do localStorage");
        }
      }
      // Busca permissões atualizadas da API
      permissions().then((data) => {
        if (isMounted && data) {
          setPermissions(data);
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [authKey]);

  const hasRole = (path?: string, jsonPermissions?: any) => {
    const permission = jsonPermissions.find((item: any) =>
      item.path?.endsWith(path)
    );

    if (permission) {
      return permission.allow;
    } else {
      return true;
    }
  };

  const hasChildPermission = (modulePath: string, childKey: string): boolean => {
    if (!permissionsData || permissionsData.length === 0) return false;

    const module = permissionsData.find((p: any) => p.path === modulePath);
    if (!module) return false;

    if (!module.children || module.children.length === 0) return false;

    const child = module.children.find((c: any) => c.key === childKey);
    return child ? child.allow : false;
  };

  const handleLogin = async (email: string, password: string) => {
    return login({ email, password })
      .then((key: string) => {
        setAuthKey(key);
        return key;
      })
      .catch((err) => {
        console.log("Erro");
        throw err;
      });
  };

  const handleLogout = async () => {
    return logout()
      .then((data) => {
        setAuthKey("");
        window.location.href = "/login";
        localStorage.clear();
        return data;
      })
      .catch((err) => {
        throw err;
      });
  };

  const handlePermission = async () => {
    return permissions()
      .then((data) => {
        setPermissions(data);
        return data;
      })
      .catch((err) => {
        throw err;
      });
  };

  function findObjectByPath(data?: any, path?: string) {
    return data?.find((item: any) => item.path.includes(path));
  }

  return (
    <AuthContext.Provider
      value={{
        authKey,
        hasRole,
        hasChildPermission,
        isLoggingIn,
        isLoggingOut,
        login: handleLogin,
        logout: handleLogout,
        permissionsList: permissionsData,
        permissions: handlePermission,
        userInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthProvider;
